import { NOT_FOUND_ERROR_404, PROPERTY_DETAILS_TAB_NAMES } from '@/constants';
import { useActivityFeed } from '@/utils/hooks/data/useActivityFeed';
import { useComplaints } from '@/utils/hooks/data/useComplaints';
import { useInspections } from '@/utils/hooks/data/useInspections';
import { useLicenses } from '@/utils/hooks/data/useLicenses';
import { usePropertyDetails } from '@/utils/hooks/data/usePropertyDetails';
import { usePropertyTaxes } from '@/utils/hooks/data/usePropertyTaxes';
import { useViolations } from '@/utils/hooks/data/useViolations';
import { useActivePropertyDetailsTab } from '@/utils/hooks/useActivePropertyDetailsTab';
import { useOptionalPropertyDeckardId } from '@/utils/hooks/usePropertyDeckardId';
import { useCallback } from 'react';
import { Nav } from 'rsuite';
import { PropertyDetailsNavWrapper } from './property-details-tabs-styles';

/**
 * If you're looking to add another item to the Property Overview nav bar and clicking on the
 * nav element doesn't work, then you may need to check "activePropertyDetailsTab" in "dashboard-selectors".
 */
export function PropertyDetailsTabs() {
  const { goToPropertyDetails, currentProperty: propertyData } =
    usePropertyDetails();
  const { propertyDeckardId } = useOptionalPropertyDeckardId();
  const { isEnabled: isComplaintsEnabled } = useComplaints();
  const { isEnabled: isViolationsEnabled } = useViolations();
  const { isEnabled: isInspectionsEnabled } = useInspections();
  const { isEnabled: isActivityFeedEnabled } = useActivityFeed();
  const { isEnabled: isPropertyTaxesEnabled } = usePropertyTaxes();
  const { isEnabled: isLicenseEnabled } = useLicenses();

  const activePropertyDetailsTab = useActivePropertyDetailsTab();

  const handleNavigateToPropertyDetailsTab = useCallback(
    (propertyDetailsTab: string) => {
      if (!propertyDeckardId) return;

      goToPropertyDetails({
        propertyDeckardId,
        propertyDetailsTab,
        replace: true,
      });
    },
    [propertyDeckardId, goToPropertyDetails]
  );

  return (
    <PropertyDetailsNavWrapper>
      <Nav
        appearance="subtle"
        activeKey={activePropertyDetailsTab}
        data-cy="property-details-modal-nav"
        onSelect={handleNavigateToPropertyDetailsTab}
        role="navigation"
      >
        <Nav.Item
          aria-label="Open Overview Page"
          data-cy={`property-details-tabs-${PROPERTY_DETAILS_TAB_NAMES.overview}`}
          disabled={propertyData?.Code === NOT_FOUND_ERROR_404}
          eventKey={PROPERTY_DETAILS_TAB_NAMES.overview}
        >
          Overview
        </Nav.Item>

        {isLicenseEnabled && (
          <Nav.Item
            aria-label="Open License Page"
            data-cy={`property-details-tabs-${PROPERTY_DETAILS_TAB_NAMES.license}`}
            eventKey={PROPERTY_DETAILS_TAB_NAMES.license}
          >
            License
          </Nav.Item>
        )}

        {isComplaintsEnabled && (
          <Nav.Item
            aria-label="Open Complaints Page"
            data-cy={`property-details-tabs-${PROPERTY_DETAILS_TAB_NAMES.complaints}`}
            eventKey={PROPERTY_DETAILS_TAB_NAMES.complaints}
          >
            Complaints
          </Nav.Item>
        )}

        {isViolationsEnabled && (
          <Nav.Item
            aria-label="Open Potential Violations Page"
            data-cy={`property-details-tabs-${PROPERTY_DETAILS_TAB_NAMES.violations}`}
            eventKey={PROPERTY_DETAILS_TAB_NAMES.violations}
          >
            Potential Violations
          </Nav.Item>
        )}

        <Nav.Item
          aria-label="Open History Page"
          data-cy={`property-details-tabs-${PROPERTY_DETAILS_TAB_NAMES.history}`}
          eventKey={PROPERTY_DETAILS_TAB_NAMES.history}
        >
          History
        </Nav.Item>

        {isInspectionsEnabled && (
          <Nav.Item
            aria-label="Open Inspections Page"
            data-cy={`property-details-tabs-${PROPERTY_DETAILS_TAB_NAMES.inspections}`}
            eventKey={PROPERTY_DETAILS_TAB_NAMES.inspections}
          >
            Inspections
          </Nav.Item>
        )}

        {isActivityFeedEnabled && (
          <Nav.Item
            aria-label="Open Activity Feed"
            data-cy={`property-details-tabs-${PROPERTY_DETAILS_TAB_NAMES.activity}`}
            eventKey={PROPERTY_DETAILS_TAB_NAMES.activity}
          >
            Activity Feed
          </Nav.Item>
        )}

        {isPropertyTaxesEnabled && (
          <Nav.Item
            aria-label="Open Tax Page"
            data-cy={`property-details-tabs-${PROPERTY_DETAILS_TAB_NAMES.propertyOverviewTaxTab}`}
            eventKey={PROPERTY_DETAILS_TAB_NAMES.propertyOverviewTaxTab}
          >
            Tax
          </Nav.Item>
        )}
      </Nav>
    </PropertyDetailsNavWrapper>
  );
}
